<template>
    <div class="apply-overlay" @click.self="$emit('close')">
        <div class="apply-modal" :dir="direction">
            
            <!-- Sticky header -->
            <div class="apply-modal-header">
                <button class="apply-close-btn" @click="$emit('close')">✕</button>
                <div>
                    <h2 class="apply-modal-title">הגשת מועמדות</h2>
                    <p class="apply-modal-subtitle">{{ ad.title }} — {{ ad.branch }}</p>
                </div>
            </div>

            <!-- Scrollable body -->
            <div class="apply-modal-body">

                <!-- Job details -->
                <div class="job-details">
                    <!-- Ad image -->
                    <div v-if="ad.image && ad.image.url" class="job-details-image">
                        <img :src="ad.image.url" :alt="ad.title" />
                    </div>

                    <!-- Title row -->
                    <div class="job-details-top">
                        <span class="job-details-badge">{{ ad.employmentType }}</span>
                        <h3 class="job-details-title">{{ ad.title }}</h3>
                        <div class="job-details-branch">
                            <i class="material-icons">place</i>
                            <span>{{ ad.branch }}</span>
                        </div>
                    </div>

                    <!-- Full description -->
                    <div v-if="ad.description">
                        <h4 class="job-details-section-title">תיאור המשרה</h4>
                        <p class="job-details-description">{{ ad.description }}</p>
                    </div>

                    <!-- All requirements -->
                    <div v-if="ad.requirements && ad.requirements.filter(r => r).length">
                        <h4 class="job-details-section-title">דרישות התפקיד</h4>
                        <ul class="job-details-requirements">
                            <li v-for="(req, i) in ad.requirements.filter(r => r)" :key="i">
                                <span class="job-req-check">✓</span>
                                {{ req }}
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Divider -->
                <div class="apply-divider">
                    <span>מלא/י את הפרטים</span>
                </div>

                <!-- Already applied banner -->
                <div v-if="alreadyApplied" class="already-applied-banner">
                    <i class="material-icons">check_circle</i>
                    <span>כבר הגשת מועמדות למשרה זו מהדפדפן הזה</span>
                </div>

                <!-- Application form -->
                <form v-if="!alreadyApplied" class="apply-form" @submit.prevent="handleSubmit" novalidate>
                    <!-- Full Name -->
                    <div v-if="config.fullName.enabled" class="apply-field">
                        <label>שם מלא<span v-if="config.fullName.required" class="req-star">*</span></label>
                        <input v-model.trim="form.fullName" type="text" placeholder="הזן שם מלא" />
                        <span v-if="errors.fullName" class="field-error">{{ errors.fullName }}</span>
                    </div>

                    <!-- Phone -->
                    <div v-if="config.phone.enabled" class="apply-field">
                        <label>טלפון<span v-if="config.phone.required" class="req-star">*</span></label>
                        <input v-model.trim="form.phone" type="tel" placeholder="050-0000000" />
                        <span v-if="errors.phone" class="field-error">{{ errors.phone }}</span>
                    </div>

                    <!-- Email -->
                    <div v-if="config.email.enabled" class="apply-field">
                        <label>דוא"ל<span v-if="config.email.required" class="req-star">*</span></label>
                        <input v-model.trim="form.email" type="email" placeholder="name@example.com" />
                        <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
                    </div>

                    <!-- ID Number -->
                    <div v-if="config.idNumber.enabled" class="apply-field">
                        <label>ת.ז.<span v-if="config.idNumber.required" class="req-star">*</span></label>
                        <input v-model.trim="form.idNumber" type="text" placeholder="9 ספרות" maxlength="9" />
                        <span v-if="errors.idNumber" class="field-error">{{ errors.idNumber }}</span>
                    </div>

                    <!-- Message -->
                    <div v-if="config.message.enabled" class="apply-field">
                        <label>ספר/י לנו על עצמך<span v-if="config.message.required" class="req-star">*</span></label>
                        <textarea v-model="form.message" rows="4" placeholder="..."></textarea>
                        <span v-if="errors.message" class="field-error">{{ errors.message }}</span>
                    </div>

                    <!-- Branch selection (optional, configured per ad) -->
                    <div v-if="config.branches.enabled" class="apply-field apply-selection">
                        <label>{{ questions.branches.title }}<span v-if="config.branches.required" class="req-star">*</span></label>
                        <p class="selection-hint">{{ questions.branches.hint }}</p>
                        <SelectionChips
                            v-model="form.selectedBranches"
                            :groups="branchGroups"
                            :availability="branchAvailability"
                            @blocked="showBlocked('branches', $event)"
                        />
                        <span v-if="blockedNotice.branches" class="blocked-notice">
                            <i class="material-icons">info</i>{{ blockedNotice.branches }}
                        </span>
                        <span v-if="errors.selectedBranches" class="field-error">{{ errors.selectedBranches }}</span>
                    </div>

                    <!-- Role selection (optional, configured per ad) -->
                    <div v-if="config.roles.enabled" class="apply-field apply-selection">
                        <label>{{ questions.roles.title }}<span v-if="config.roles.required" class="req-star">*</span></label>
                        <p class="selection-hint">{{ questions.roles.hint }}</p>
                        <SelectionChips
                            v-model="form.selectedRoles"
                            :groups="roleGroups"
                            :availability="roleAvailability"
                            @blocked="showBlocked('roles', $event)"
                        />
                        <span v-if="blockedNotice.roles" class="blocked-notice">
                            <i class="material-icons">info</i>{{ blockedNotice.roles }}
                        </span>
                        <span v-if="errors.selectedRoles" class="field-error">{{ errors.selectedRoles }}</span>
                    </div>

                    <!-- CV Upload -->
                    <div v-if="config.cv.enabled" class="apply-field">
                        <label>קורות חיים (PDF / Word)<span v-if="config.cv.required" class="req-star">*</span></label>
                        <input type="file" accept=".pdf,.doc,.docx" @change="handleFileChange" class="file-input" />
                        <span v-if="errors.cv" class="field-error">{{ errors.cv }}</span>
                    </div>

                    <div v-if="submitError" class="submit-error">{{ submitError }}</div>

                    <button type="submit" class="apply-submit-btn" :disabled="isPending">
                        <span v-if="isPending" class="spinner"></span>
                        <span>{{ isPending ? 'שולח...' : 'שלח מועמדות' }}</span>
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { projectFunctions } from '@/firebase/config'
import use_job_ads_storage from '@/Methods/use_job_ads_storage'
import { slide_pop_successs, slide_pop_error } from '@/Methods/Msgs'
import { normalizeApplyFormConfig, getOptionAvailability, validateSelection, groupedOptions, QUESTIONS } from '@/Methods/job_ads_options'
import SelectionChips from './SelectionChips.vue'

export default {
    name: 'ApplyForm',
    components: { SelectionChips },
    props: {
        ad: { type: Object, required: true }
    },
    emits: ['close'],
    setup(props, { emit }) {
        const { uploadCV, cvUrl, cvPath } = use_job_ads_storage()
        const isPending = ref(false)
        const submitError = ref('')
        const cvFile = ref(null)
        const alreadyApplied = ref(false)

        const lsKey = `jobapp_${props.ad.id}`

        onMounted(() => {
            if (localStorage.getItem(lsKey)) {
                alreadyApplied.value = true
            }
        })

        const direction = computed(() => props.ad.language === 'ru' ? 'ltr' : 'rtl')
        // Fills defaults for ads saved before newer options existed (branches / roles → disabled)
        const config = computed(() => normalizeApplyFormConfig(props.ad.applyFormConfig))

        const form = ref({
            fullName: '',
            phone: '',
            email: '',
            idNumber: '',
            message: '',
            selectedBranches: [],
            selectedRoles: []
        })
        const errors = ref({})

        // --- Optional branch / role questions (configured per ad) ---
        const questions = QUESTIONS
        const branchGroups = computed(() => groupedOptions('branches', config.value.branches.options))
        const roleGroups = computed(() => groupedOptions('roles', config.value.roles.options))
        // Which options are still selectable given the current picks (order-independent)
        const branchAvailability = computed(() => getOptionAvailability(form.value.selectedBranches, config.value.branches, 'branches'))
        const roleAvailability = computed(() => getOptionAvailability(form.value.selectedRoles, config.value.roles, 'roles'))

        // Short explanation shown when a greyed-out chip is tapped
        const blockedNotice = ref({ branches: '', roles: '' })
        const blockedTimers = {}
        const showBlocked = (kind, { label, blockedBy }) => {
            blockedNotice.value[kind] = `לא ניתן לשלב את ${label} עם ${blockedBy.join(', ')}`
            clearTimeout(blockedTimers[kind])
            blockedTimers[kind] = setTimeout(() => { blockedNotice.value[kind] = '' }, 3500)
        }
        onBeforeUnmount(() => Object.values(blockedTimers).forEach(clearTimeout))

        const handleFileChange = (e) => {
            cvFile.value = e.target.files[0] || null
        }

        const validate = () => {
            const e = {}
            const c = config.value

            if (c.fullName.enabled && c.fullName.required && !form.value.fullName)
                e.fullName = 'שדה חובה'
            if (c.phone.enabled && c.phone.required && !form.value.phone)
                e.phone = 'שדה חובה'
            if (c.phone.enabled && form.value.phone && !/^0\d{8,9}$/.test(form.value.phone.replace(/[\s\-()]/g, '')))
                e.phone = 'מספר טלפון ישראלי לא תקין (לדוגמה: 050-0000000)'
            if (c.email.enabled && c.email.required && !form.value.email)
                e.email = 'שדה חובה'
            if (c.email.enabled && form.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email))
                e.email = 'כתובת דוא"ל לא תקינה'
            if (c.idNumber.enabled && c.idNumber.required && !form.value.idNumber)
                e.idNumber = 'שדה חובה'
            if (c.idNumber.enabled && form.value.idNumber && !/^\d{9}$/.test(form.value.idNumber))
                e.idNumber = 'ת.ז. חייבת להכיל בדיוק 9 ספרות'
            if (c.message.enabled && c.message.required && !form.value.message)
                e.message = 'שדה חובה'
            if (c.cv.enabled && c.cv.required && !cvFile.value)
                e.cv = 'יש לצרף קורות חיים'

            // Same rule logic the server applies (required / offered / blocked pairs)
            const branchesError = validateSelection(form.value.selectedBranches, c.branches, 'branches')
            if (branchesError) e.selectedBranches = branchesError
            const rolesError = validateSelection(form.value.selectedRoles, c.roles, 'roles')
            if (rolesError) e.selectedRoles = rolesError

            errors.value = e
            return Object.keys(e).length === 0
        }

        const GENERIC_ERROR = 'אירעה שגיאה. נסה שוב.'

        // Maps HttpsError codes thrown by submit_job_application to what the candidate sees
        const handleSubmitError = (err) => {
            const code = err && err.code ? String(err.code).replace('functions/', '') : ''
            const serverMessage = err && err.message ? err.message : ''
            const field = err && err.details && err.details.field
            if (code === 'already-exists') {
                submitError.value = 'מספר הטלפון כבר הגיש מועמדות למשרה זו'
            } else if (code === 'invalid-argument' || code === 'failed-precondition' || code === 'not-found') {
                submitError.value = serverMessage || GENERIC_ERROR
                if (field) errors.value = { ...errors.value, [field]: serverMessage }
                slide_pop_error(submitError.value)
            } else {
                submitError.value = GENERIC_ERROR
                slide_pop_error('אירעה שגיאה בשליחת הטופס')
            }
        }

        const handleSubmit = async () => {
            submitError.value = ''
            if (!validate()) return

            isPending.value = true
            try {
                let uploadedCvUrl = ''
                let uploadedCvPath = ''

                if (config.value.cv.enabled && cvFile.value) {
                    await uploadCV(cvFile.value, props.ad.id)
                    uploadedCvUrl = cvUrl.value
                    uploadedCvPath = cvPath.value
                }

                // The Cloud Function re-validates every field and the branch/role selections
                // against this ad's configuration, runs the phone-based dedup check and writes
                // the application (clients cannot write JobApplications directly).
                const submit = projectFunctions.httpsCallable('submit_job_application', { timeout: 60000 })
                await submit({
                    adId: props.ad.id,
                    fullName: form.value.fullName,
                    phone: form.value.phone,
                    email: form.value.email,
                    idNumber: form.value.idNumber,
                    message: form.value.message,
                    cvUrl: uploadedCvUrl,
                    cvPath: uploadedCvPath,
                    selectedBranches: config.value.branches.enabled ? [...form.value.selectedBranches] : [],
                    selectedRoles: config.value.roles.enabled ? [...form.value.selectedRoles] : []
                })

                // Save to localStorage (browser-based)
                localStorage.setItem(lsKey, '1')

                slide_pop_successs('הטופס נשלח בהצלחה!')
                emit('close')
            } catch (err) {
                handleSubmitError(err)
            } finally {
                isPending.value = false
            }
        }

        return {
            direction, config, form, errors, cvFile, isPending, submitError, alreadyApplied,
            questions, branchGroups, roleGroups, branchAvailability, roleAvailability, blockedNotice, showBlocked,
            handleFileChange, handleSubmit
        }
    }
}
</script>

<style scoped>
.apply-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9000;
    padding: 16px;
}
.apply-modal {
    background: #ffffff;
    border-radius: 16px;
    width: 100%;
    max-width: 560px;
    max-height: 90vh;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
@media (max-width: 600px) {
    .apply-overlay {
        padding: 8px;
    }
    .apply-modal {
        max-height: 96vh;
    }
}

/* Sticky header */
.apply-modal-header {
    background: #FEF200;
    padding: 16px 20px;
    color: #000;
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex-shrink: 0;
    border-bottom: 3px solid #000;
}
.apply-close-btn {
    background: rgba(0, 0, 0, 0.08);
    border: 1.5px solid rgba(0, 0, 0, 0.18);
    color: #000;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}
.apply-close-btn:hover {
    background: rgba(0, 0, 0, 0.15);
}
.apply-modal-title {
    font-size: 20px;
    font-weight: 800;
    margin: 0 0 3px;
    color: #000;
}
.apply-modal-subtitle {
    font-size: 15px;
    color: #000;
    opacity: 0.7;
    margin: 0;
}

/* Scrollable body */
.apply-modal-body {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

/* Job details */
.job-details {
    padding: 20px 20px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.job-details-image {
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
}
.job-details-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.job-details-top {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.job-details-badge {
    display: inline-block;
    background: #FEF200;
    color: #000;
    border: 1.5px solid #000;
    border-radius: 50px;
    font-size: 14px;
    padding: 4px 16px;
    align-self: flex-start;
    font-weight: 700;
    white-space: nowrap;
}
.job-details-title {
    font-size: 26px;
    font-weight: 800;
    color: #000;
    margin: 0;
    line-height: 1.2;
}
.job-details-branch {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    color: #555;
    font-weight: 600;
}
.job-details-branch i {
    font-size: 18px;
    color: #888;
}
.job-details-section-title {
    font-size: 16px;
    font-weight: 800;
    color: #000;
    margin: 0 0 8px;
    padding-bottom: 5px;
    border-bottom: 2px solid #FEF200;
    display: inline-block;
}
.job-details-description {
    font-size: 16px;
    color: #333;
    margin: 0;
    line-height: 1.7;
    white-space: pre-line;
}
.job-details-requirements {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 9px;
}
.job-details-requirements li {
    font-size: 16px;
    color: #222;
    display: flex;
    align-items: flex-start;
    gap: 9px;
    line-height: 1.5;
}
.job-req-check {
    color: #000;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 2px;
}

/* Divider */
.apply-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 20px 4px;
    font-size: 13px;
    font-weight: 700;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.apply-divider::before,
.apply-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
}

/* Form */
.apply-form {
    padding: 0 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.apply-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.apply-field label {
    font-size: 16px;
    font-weight: 600;
    color: #222;
}
.apply-selection .selection-hint {
    margin: -2px 0 6px;
    font-size: 13px;
    color: #666;
}
.blocked-notice {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    margin-top: 6px;
    padding: 6px 10px;
    border-radius: 8px;
    background: #FFF8C4;
    border: 1px solid #F0DE5A;
    color: #6b5300;
    font-size: 13px;
    font-weight: 600;
}
.blocked-notice i {
    font-size: 16px;
}
.req-star {
    color: #bb2429;
    margin-right: 2px;
}
.apply-field input[type="text"],
.apply-field input[type="tel"],
.apply-field input[type="email"],
.apply-field textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 13px 18px;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    font-size: 16px;
    color: #333;
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
    font-family: inherit;
}
.apply-field textarea {
    border-radius: 12px;
    resize: vertical;
}
.apply-field input:focus,
.apply-field textarea:focus {
    border-color: #1579F2;
    box-shadow: 0 0 0 3px rgba(21, 121, 242, 0.12);
}
.file-input {
    padding: 8px 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
}
.field-error {
    font-size: 12px;
    color: #bb2429;
    padding-right: 4px;
}
.submit-error {
    background: #fff0f0;
    border: 1px solid #bb2429;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: #bb2429;
    text-align: center;
}

/* Already applied */
.already-applied-banner {
    margin: 20px 20px 24px;
    background: #f0fdf4;
    border: 1.5px solid #22c55e;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 16px;
    font-weight: 600;
    color: #166534;
}
.already-applied-banner i {
    font-size: 28px;
    color: #22c55e;
    flex-shrink: 0;
}

/* Submit button */
.apply-submit-btn {
    width: 220px;
    height: 54px;
    background: #FEF200;
    color: #000;
    border: 2px solid #000;
    border-radius: 50px;
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s, transform 0.1s;
    margin-top: 4px;
    box-shadow: 3px 3px 0px #000;
}
.apply-submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 4px 5px 0px #000;
}
.apply-submit-btn:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 1px 1px 0px #000;
}
.apply-submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Spinner */
.spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid #000;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
